import React, { useMemo } from 'react';
import {
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FixedSizeGrid as Grid } from 'react-window';
import { UserProfile } from 'src/interfaces/user';

interface Role {
  id: string;
  name: string;
}

interface Organization {
  id: string;
  name: string;
  roles: Role[];
}

interface Props {
  selectedOrganizations: Organization[];
  publicRoles: Role[];
  handleRoleChange: (orgId: string, roles: Role[], isNew?: boolean) => void;
  handleDeleteOrganization: (orgId: string) => void;
  selectedUser: UserProfile;
  activeTab :number;
  currentUserClient:{};
}

const LazyLoadedOrganizationGrid: React.FC<Props> = ({
  selectedOrganizations,
  publicRoles,
  handleRoleChange,
  handleDeleteOrganization,
}) => {
  // Define grid properties
  const columnCount = window.innerWidth > 768 ? 2 : 1; // 2 columns for larger screens, 1 column for smaller
  const rowCount = Math.ceil(selectedOrganizations.length / columnCount); // Calculate total rows
  const containerWidth = window.innerWidth > 768 ? 800 : 400; // Adjust container width dynamically

  // Memoize grid width for performance
  const columnWidth = useMemo(() => containerWidth / columnCount, [containerWidth, columnCount]);
  const rowHeight = 200; // Fixed row height

  // Cell Renderer for the grid
  const Cell = ({
    columnIndex,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    rowIndex: number;
    style: React.CSSProperties;
  }) => {
    const index = rowIndex * columnCount + columnIndex; // Calculate the index of the organization
    if (index >= selectedOrganizations.length) return null; // Handle out-of-bounds cells

    const org = selectedOrganizations[index];

    return (
      <div style={{ ...style, padding: '16px', boxSizing: 'border-box' }} key={org.id}>
        <div className="flex flex-col space-y-2 border p-4 rounded-md">
          <div className="flex justify-between items-center">
            <span className="font-medium text-lg">{org.name}</span>
            <IconButton onClick={() => handleDeleteOrganization(org.id)} color="secondary">
              <DeleteIcon />
            </IconButton>
          </div>
          <FormControl fullWidth>
            <InputLabel>Change Roles</InputLabel>
            <Select
              multiple
              value={org.roles}
              onChange={(e) =>
                handleRoleChange(
                  org.id,
                  (e.target.value as Role[]).map((role) => role),
                  false
                )
              }
              renderValue={() => <div>Add/Remove</div>}
            >
              {publicRoles
                .filter((frole) => !org.roles.some((srole) => srole.id === frole.id))
                .map((role) => (
                  <MenuItem key={role.id} value={role}>
                    {role.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <div className="flex flex-wrap gap-2">
            {org.roles.map((role) => (
              <Chip
                key={role.id}
                label={role.name}
                onDelete={() =>
                  handleRoleChange(
                    org.id,
                    org.roles.filter((r) => r.id !== role.id),
                    false
                  )
                }
                className="bg-orange-500 text-white"
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Grid
      columnCount={columnCount}
      rowCount={rowCount}
      columnWidth={columnWidth}
      rowHeight={rowHeight}
      height={600} // Set container height
      width={containerWidth} // Use dynamic width
    >
      {Cell}
    </Grid>
  );
};

export default LazyLoadedOrganizationGrid;
